import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { FREELANCER_LOGIN_PAGE } from "../../auth/routes";
import {
  FREELANCER_SCHEDULED_STORES_PAGE,
} from "../routes";

import { getMyFreelancerStoresApi, FreelancerStore } from "../freelancerApi";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  listMyRegistrationsApi,
  MyRegistrationItem,
} from "../../scheduling/schedulingApi";

import "./freelancer-scheduled-store-slots.css";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildIsoUtc(dayYmd: string, hhmm: string): string {
  return new Date(`${dayYmd}T${hhmm}:00`).toISOString();
}

function formatTimeFromIso(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ymdFromIsoOrYmd(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const ACTIVE_WORK_STATUSES = new Set(["PENDENTE", "APROVADO"]);

const FreelancerScheduledStoreSlotsPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(FREELANCER_SCHEDULED_STORES_PAGE());
  const { storeId } = useParams<{ storeId: string }>();

  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  const [loadingStore, setLoadingStore] = useState(false);
  const [store, setStore] = useState<FreelancerStore | null>(null);

  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const todayYmd = useMemo(() => toYmd(today), [today]);
  const currentWeekStart = useMemo(() => startOfWeekMonday(today), [today]);

  const canGoPrevWeek = useMemo(() => {
    return weekStart.getTime() > currentWeekStart.getTime();
  }, [weekStart, currentWeekStart]);

  const daysOfWeek = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    return labels.map((label, idx) => {
      const d = addDays(weekStart, idx);
      return { label, date: d, ymd: toYmd(d) };
    });
  }, [weekStart]);

  const [selectedDayYmd, setSelectedDayYmd] = useState<string>(() => toYmd(new Date()));

  const [loadingRegs, setLoadingRegs] = useState(false);
  const [weekRegs, setWeekRegs] = useState<MyRegistrationItem[]>([]);

  const hasAuth = useMemo(() => Boolean(token), [token]);

  const fetchMyStore = useCallback(async () => {
    if (!token) return;
    if (!storeId) return;

    try {
      setLoadingStore(true);
      const data = await getMyFreelancerStoresApi(token);
      const found = (data.items ?? []).find((s) => s.id === storeId) ?? null;

      if (!found) {
        toast.error("Você não tem acesso a essa loja.");
        navigate(FREELANCER_SCHEDULED_STORES_PAGE(), { replace: true });
        return;
      }

      setStore(found);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao carregar loja.");
    } finally {
      setLoadingStore(false);
    }
  }, [token, storeId, navigate]);

  const fetchWeekScheduled = useCallback(async () => {
    if (!token) return;
    if (!storeId) return;

    try {
      setLoadingRegs(true);

      const weekStartYmd = toYmd(weekStart);
      const weekEndYmd = toYmd(addDays(weekStart, 7));

      const from = buildIsoUtc(weekStartYmd, "00:00");
      const to = buildIsoUtc(weekEndYmd, "00:00");

      const myRegs = await listMyRegistrationsApi(token, { from, to });

      const now = Date.now();

      const filtered = (myRegs ?? [])
        .filter((r) => r?.slot?.store?.id === storeId)
        .filter((r) => ACTIVE_WORK_STATUSES.has(String(r.status)))
        .filter((r) => new Date(r.slot.startAt).getTime() > now)
        .sort((a, b) => a.slot.startAt.localeCompare(b.slot.startAt));

      setWeekRegs(filtered);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao listar sua agenda.");
    } finally {
      setLoadingRegs(false);
    }
  }, [token, storeId, weekStart]);

  useEffect(() => {
    if (!hasAuth) {
      toast.error("Você precisa estar logado.");
      navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
      return;
    }
    if (!storeId) {
      navigate(FREELANCER_SCHEDULED_STORES_PAGE(), { replace: true });
      return;
    }
    fetchMyStore();
  }, [hasAuth, storeId, navigate, fetchMyStore]);

  useEffect(() => {
    if (!store) return;

    const inWeek = daysOfWeek.some((d) => d.ymd === selectedDayYmd);
    const isPast = selectedDayYmd < todayYmd;

    if (!inWeek || isPast) {
      const firstAllowed = daysOfWeek.find((d) => d.ymd >= todayYmd) ?? daysOfWeek[0];
      setSelectedDayYmd(firstAllowed.ymd);
    }
  }, [store, daysOfWeek, selectedDayYmd, todayYmd]);

  useEffect(() => {
    if (!store) return;
    fetchWeekScheduled();
  }, [store, fetchWeekScheduled]);

  const regsByDay = useMemo(() => {
    const map: Record<string, MyRegistrationItem[]> = {};
    for (const r of weekRegs) {
      const dayYmd = ymdFromIsoOrYmd(r.slot.day);
      if (!map[dayYmd]) map[dayYmd] = [];
      map[dayYmd].push(r);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => a.slot.startAt.localeCompare(b.slot.startAt));
    }
    return map;
  }, [weekRegs]);

  const selectedDayRegs = useMemo(() => {
    return regsByDay[selectedDayYmd] ?? [];
  }, [regsByDay, selectedDayYmd]);

  if (!hasAuth) return null;

  return (
    <div className="FreelancerSlotsPage">
      <div className="StoreMenuPage">
        <div className="StoreMenuContainer">
          <h1 className="StoreMenuTitle">Minha Agenda</h1>

          {loadingStore ? (
            <div className="FreelancerSlotsCard">
              <div className="FreelancerSlotsEmpty">Carregando loja...</div>
            </div>
          ) : !store ? (
            <div className="FreelancerSlotsCard">
              <div className="FreelancerSlotsEmpty">Loja não encontrada.</div>
            </div>
          ) : (
            <>
              <div className="FreelancerSlotsStoreCard">
                <div className="SelectedInfoBox">
                  <div className="SelectedInfoRow">
                    <span className="SelectedInfoLabel">Loja</span>
                    <span className="SelectedInfoValue">{store.nomeFantasia}</span>
                  </div>

                  <div className="SelectedInfoRow">
                    <span className="SelectedInfoLabel">Endereço</span>
                    <span className="SelectedInfoValue">
                      {store.endereco ? store.endereco : "Endereço não informado"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="FreelancerSlotsCard">
                <div className="WeekHeader">
                  <button
                    type="button"
                    className={`PagerButton ${canGoPrevWeek ? "" : "PagerButtonLight"}`}
                    onClick={() => setWeekAnchor((d) => addDays(d, -7))}
                    disabled={!canGoPrevWeek || loadingRegs}
                  >
                    Semana anterior
                  </button>

                  <div className="WeekTitle">
                    Semana de <b>{formatBrDate(toYmd(weekStart))}</b>
                  </div>

                  <button
                    type="button"
                    className="PagerButton"
                    onClick={() => setWeekAnchor((d) => addDays(d, 7))}
                    disabled={loadingRegs}
                  >
                    Próxima semana
                  </button>
                </div>

                <div className="DayStrip">
                  {daysOfWeek.map((d) => {
                    const active = d.ymd === selectedDayYmd;
                    const isPastDay = d.ymd < todayYmd;

                    const dayRegs = regsByDay[d.ymd] ?? [];
                    const count = dayRegs.length;

                    return (
                      <button
                        key={d.ymd}
                        type="button"
                        className={`DayPill ${
                          count === 0 ? "DayPillGray" : "DayPillOrange"
                        } ${active ? "DayPillActive" : ""}`}
                        onClick={() => setSelectedDayYmd(d.ymd)}
                        disabled={isPastDay || loadingRegs}
                        title={isPastDay ? "Dia já passou" : undefined}
                      >
                        <div className="DayPillTop">{d.label}</div>
                        <div className="DayPillBottom">{formatBrDate(d.ymd)}</div>
                        <div className="DayPillCount">{count} agendamento(s)</div>
                      </button>
                    );
                  })}
                </div>

                <div className="Box">
                  <div className="BoxTitle">
                    Horários do dia <b>{formatBrDate(selectedDayYmd)}</b>
                  </div>

                  {loadingRegs ? (
                    <div className="BoxEmpty">Carregando horários...</div>
                  ) : selectedDayRegs.length === 0 ? (
                    <div className="BoxEmpty">Nenhum horário agendado para este dia.</div>
                  ) : (
                    <div className="DraftList">
                      {selectedDayRegs.map((r) => {
                        const s = r.slot;
                        const endIsNextDay = s.endAt.slice(0, 10) !== s.startAt.slice(0, 10);
                        const endLabel = endIsNextDay
                          ? `${formatTimeFromIso(s.endAt)} (+1)`
                          : formatTimeFromIso(s.endAt);

                        return (
                          <div key={r.id} className="DraftItem">
                            <div className="DraftInfo">
                              <div className="DraftTitle">
                                {formatTimeFromIso(s.startAt)} - {endLabel}
                              </div>
                              <div className="DraftSub">
                                Agendado
                              </div>
                            </div>

                            <div className="FreelancerScheduleRight">
                              <div className="FreelancerScheduleSmall">
                                {new Date(s.startAt).toLocaleDateString("pt-BR")}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="StoreMenuFooter">
            <button
              type="button"
              className="StoreMenuSecondaryButton"
              onClick={() => navigate("/")}
            >
              Home
            </button>

            <button
              type="button"
              className="StoreMenuLogout"
              onClick={goBack}
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerScheduledStoreSlotsPage;
